import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-localization": "en",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

// ====================================
// REQUEST INTERCEPTOR
// ====================================

api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ====================================
// RESPONSE INTERCEPTOR
// ====================================

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

   // Don't refresh token for login or refresh APIs
if (
  originalRequest?.url?.includes("login-panel/login") ||
  originalRequest?.url?.includes("login-panel/refresh-access-token")
) {
  return Promise.reject(error);
}

    if (
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve,
            reject,
          });
        })
          .then((token) => {
            originalRequest.headers.Authorization =
              `Bearer ${token}`;

            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken =
          localStorage.getItem("refreshToken");

        if (!refreshToken) {
          throw new Error(
            "Refresh token missing"
          );
        }

        const response = await axios.post(
          `${BASE_URL}login-panel/refresh-access-token`,
          {
            refreshToken,
          }
        );

        const newAccessToken =
          response.data?.accessToken ||
          response.data?.data?.accessToken;

        if (!newAccessToken) {
          throw new Error(
            "New access token not found"
          );
        }

        localStorage.setItem(
          "accessToken",
          newAccessToken
        );

        processQueue(
          null,
          newAccessToken
        );

        originalRequest.headers.Authorization =
          `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        localStorage.removeItem(
          "accessToken"
        );
        localStorage.removeItem(
          "refreshToken"
        );

        window.location.href = "/";

        return Promise.reject(
          refreshError
        );
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ====================================
// NEW STYLE METHODS
// ====================================

export const GetData = async (
  endpoint,
  params = {}
) => {
  const response = await api.get(
    endpoint,
    {
      params,
    }
  );

  return response.data;
};

export const PostData = async (
  endpoint,
  data = {}
) => {
  const response = await api.post(
    endpoint,
    data
  );

  return response.data;
};

export const PutData = async (
  endpoint,
  data = {}
) => {
  const response = await api.put(
    endpoint,
    data
  );

  return response.data;
};

export const DeleteData = async (
  endpoint
) => {
  const response =
    await api.delete(endpoint);

  return response.data;
};

// ====================================
// TOKEN METHODS
// ====================================

export const GetDataWithToken =
  async (
    endpoint,
    params = {}
  ) => {
    try {
      // OLD FORMAT
      if (
        typeof params ===
          "string" &&
        endpoint?.startsWith(
          "http"
        )
      ) {
        const response =
          await axios.get(
            endpoint + params,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "accessToken"
                )}`,
              },
            }
          );

        return response.data;
      }

      // NEW FORMAT
      const response =
        await api.get(endpoint, {
          params,
        });

      return response.data;
    } catch (error) {
      return (
        error?.response?.data ||
        error
      );
    }
  };

export const PostDataWithToken =
  async (
    endpoint,
    data = {}
  ) => {
    try {
      const response =
        await api.post(
          endpoint,
          data
        );

      return response.data;
    } catch (error) {
      return (
        error?.response?.data ||
        error
      );
    }
  };

export const PutDataWithToken =
  async (
    endpoint,
    data = {}
  ) => {
    try {
      const response =
        await api.put(
          endpoint,
          data
        );

      return response.data;
    } catch (error) {
      return (
        error?.response?.data ||
        error
      );
    }
  };
  export const UploadFileWithToken = async (endpoint, formData) => {
  const res = await api.post(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

export const DeleteDataWithToken =
  async (endpoint) => {
    try {
      const response =
        await api.delete(
          endpoint
        );

      return response.data;
    } catch (error) {
      return (
        error?.response?.data ||
        error
      );
    }
  };

// ====================================
// FILE UPLOAD
// ====================================

export const PostImageDataWithToken =
  async (
    endpoint,
    formData
  ) => {
    try {
      const response =
        await api.post(
          endpoint,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      return response.data;
    } catch (error) {
      return (
        error?.response?.data ||
        error
      );
    }
  };

export default api;